import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Package, GitBranch, ScrollText, Settings, LogOut,
  GitCompare, Upload, Menu, X, BarChart2, ShieldCheck,
  ChevronsLeft, ChevronsRight, Zap,
} from "lucide-react";import { useAuth } from "@/context/AuthContext";
import { usePlatform } from "@/context/PlatformContext";
import { JasrMark } from "@/components/JasrLogo";

const navGroups = [
  {
    label: "عام",
    items: [
      { to: "/dashboard", label: "لوحة التحكم", icon: LayoutDashboard, exact: true, testid: "nav-dashboard" },
      { to: "/analytics", label: "التحليلات", icon: BarChart2, testid: "nav-analytics" },
      { to: "/preview", label: "معاينة المطابقة", icon: GitCompare, testid: "nav-preview" },
    ],
  },
  {
    label: "المتجر",
    items: [
      { to: "/import", label: "استيراد Excel", icon: Upload, testid: "nav-import" },
      { to: "/products", label: "المنتجات", icon: Package, testid: "nav-products" },
      { to: "/branches", label: "الفروع", icon: GitBranch, testid: "nav-branches" },
      { to: "/optimizer", label: "محرك النمو", icon: Zap, testid: "nav-optimizer", highlight: true },
    ],
  },
  {
    label: "النظام",
    items: [
      { to: "/logs", label: "سجل التزامن", icon: ScrollText, testid: "nav-logs" },
      { to: "/settings", label: "الإعدادات", icon: Settings, testid: "nav-settings" },
    ],
  },
];

function NavItems({ isCollapsed, onNavigate }) {
  const { user } = useAuth();
  return (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 space-y-1 scrollbar-hide" data-testid="main-nav">
      {navGroups.map((group) => (
        <div key={group.label} className="mb-2">
          {!isCollapsed && (
            <p className="text-[9px] tracking-[0.18em] uppercase font-bold text-white/20 px-4 mb-1 mt-2">
              {group.label}
            </p>
          )}
          {isCollapsed && <div className="h-px mx-3 bg-white/[0.06] mb-2 mt-2" />}
          <div className="space-y-0.5 px-2">
            {group.items.map((item) => (
              <div key={item.to} className="relative group/item">
                {item.highlight ? (
                  /* ── محرك النمو — special glow item ── */
                  <NavLink
                    to={item.to}
                    end={item.exact}
                    onClick={onNavigate}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-xl font-medium transition-all duration-200 overflow-hidden
                      ${isCollapsed ? "justify-center h-10 w-10 mx-auto" : "px-3 h-10 w-full"}
                      ${isActive
                        ? "text-white shadow-[0_0_0_1px_rgba(0,78,235,0.4)]"
                        : "text-white/80 hover:text-white"
                      }`
                    }
                    style={({ isActive }) => ({
                      background: isActive
                        ? "linear-gradient(135deg, #004EEB 0%, #0066FF 100%)"
                        : "linear-gradient(135deg, rgba(0,78,235,0.18) 0%, rgba(0,102,255,0.08) 100%)",
                      border: "1px solid rgba(0,78,235,0.3)",
                    })}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={`shrink-0 transition-colors ${isCollapsed ? "w-[18px] h-[18px]" : "w-4 h-4"} ${
                            isActive ? "text-white" : "text-[#60A5FA]"
                          }`}
                        />
                        {!isCollapsed && (
                          <>
                            <span className="text-[13px] leading-none flex-1">{item.label}</span>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                              style={{ background: "rgba(96,165,250,0.2)", color: "#93C5FD" }}>
                              AI
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </NavLink>
                ) : (
                <NavLink
                  to={item.to}
                  end={item.exact}
                  onClick={onNavigate}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl font-medium transition-all duration-200 overflow-hidden
                    ${isCollapsed ? "justify-center h-10 w-10 mx-auto" : "px-3 h-10 w-full"}
                    ${isActive
                      ? "bg-[#004EEB]/15 text-white shadow-[0_0_0_1px_rgba(0,78,235,0.2)]"
                      : "text-white/45 hover:text-white/90 hover:bg-[var(--bg-card-hover)]/[0.05]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        className={`shrink-0 transition-colors ${isCollapsed ? "w-[18px] h-[18px]" : "w-4 h-4"} ${
                          isActive ? "text-[#5b93ff]" : "text-white/35 group-hover/item:text-white/70"
                        }`}
                      />
                      {!isCollapsed && (
                        <>
                          <span className="text-[13px] leading-none flex-1">{item.label}</span>
                          {isActive && (
                            <span className="w-1 h-4 rounded-full bg-[#5b93ff] me-0.5 pulse-dot shrink-0" />
                          )}
                        </>
                      )}
                    </>
                  )}
                </NavLink>
                )}

                {/* Floating tooltip on collapsed */}
                {isCollapsed && (
                  <div className="pointer-events-none absolute end-full top-1/2 -translate-y-1/2 me-3 z-50 hidden group-hover/item:flex items-center">
                    <div className="bg-[#1a2744] border border-white/10 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap">
                      {item.label}
                    </div>
                    <div className="w-1.5 h-1.5 bg-[#1a2744] border-e border-t border-white/10 rotate-45 -ms-1" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Admin section */}
      {user?.role === "admin" && (
        <div className="px-2 mt-1">
          {!isCollapsed && (
            <p className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#5b93ff]/40 px-2 mb-1 mt-2">
              الإدارة
            </p>
          )}
          <div className="relative group/item">
            <NavLink
              to="/admin"
              onClick={onNavigate}
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl font-medium transition-all duration-200 overflow-hidden
                ${isCollapsed ? "justify-center h-10 w-10 mx-auto" : "px-3 h-10 w-full"}
                ${isActive
                  ? "bg-[#004EEB]/20 text-white"
                  : "text-[#5b93ff]/60 hover:text-[#5b93ff] hover:bg-[#004EEB]/08"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <ShieldCheck className={`shrink-0 ${isCollapsed ? "w-[18px] h-[18px]" : "w-4 h-4"} ${isActive ? "text-[#5b93ff]" : "text-[#5b93ff]/50"}`} />
                  {!isCollapsed && <span className="text-[13px] leading-none flex-1">إدارة المستأجرين</span>}
                  {!isCollapsed && isActive && <span className="w-1 h-4 rounded-full bg-[#5b93ff] shrink-0 pulse-dot" />}
                </>
              )}
            </NavLink>
            {isCollapsed && (
              <div className="pointer-events-none absolute end-full top-1/2 -translate-y-1/2 me-3 z-50 hidden group-hover/item:flex items-center">
                <div className="bg-[#1a2744] border border-white/10 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap">
                  إدارة المستأجرين
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

function PlatformLogo({ size = 22 }) {
  const { logo_base64 } = usePlatform();
  if (logo_base64) {
    return <img src={logo_base64} alt="logo" style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  return <JasrMark size={size} />;
}

const sidebarBg = "bg-[#080F1A]";

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { name, tagline } = usePlatform();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebar_collapsed", String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const initials = (user?.name || "م").slice(0, 1);

  return (
    <>
      {/* ── Mobile top bar ── */}
      <div
        className={`md:hidden fixed top-0 inset-x-0 z-30 h-14 ${sidebarBg} border-b border-white/[0.07] flex items-center justify-between px-4`}
        data-testid="mobile-topbar"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-[#004EEB] rounded-xl grid place-items-center shadow-[0_0_14px_rgba(0,78,235,0.45)]">
            <PlatformLogo size={20} />
          </div>
          <span className="text-white font-black text-sm tracking-tight">{name}<span className="text-[#004EEB]">.</span></span>
        </div>
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-button"
          className="p-2 rounded-xl border border-white/10 text-white/60 hover:bg-[var(--bg-card-hover)]/5 hover:text-white transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* ── Desktop sidebar ── */}
      <aside
        className={`hidden md:flex flex-col ${sidebarBg} border-s border-white/[0.07] h-full relative overflow-hidden
          transition-all duration-300 ease-in-out shrink-0
          ${isCollapsed ? "w-[76px]" : "w-64"}`}
        data-testid="desktop-sidebar"
      >
        {/* Top gradient accent */}
        <div className="absolute top-0 inset-x-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(0,78,235,0.06) 0%, transparent 100%)" }} />

        {/* Brand */}
        <div className={`h-20 flex items-center shrink-0 transition-all duration-300 ${isCollapsed ? "justify-center px-0" : "gap-3 px-4"}`}>
          <div className="w-11 h-11 bg-[#004EEB] rounded-xl grid place-items-center shrink-0 shadow-[0_0_20px_rgba(0,78,235,0.4)] glow-blue">
            <PlatformLogo size={26} />
          </div>
          {!isCollapsed && (
            <>
              <div className="leading-tight overflow-hidden flex-1">
                <div className="text-white font-black text-[18px] tracking-tight whitespace-nowrap">
                  {name}<span className="text-[#004EEB]">.</span>
                </div>
                <div className="text-white/50 text-[11px] tracking-wide whitespace-nowrap mt-0.5">{tagline}</div>
              </div>
              <button
                onClick={toggleCollapse}
                data-testid="sidebar-collapse-toggle"
                title="طي القائمة"
                className="group/toggle w-8 h-8 rounded-lg flex items-center justify-center text-white/25 hover:text-white/70 hover:bg-[var(--bg-card-hover)]/[0.06] transition-all duration-200 shrink-0"
              >
                <ChevronsRight className="w-4 h-4 transition-transform group-hover/toggle:scale-110" />
              </button>
            </>
          )}
          {isCollapsed && (
            <button
              onClick={toggleCollapse}
              data-testid="sidebar-collapse-toggle"
              title="توسيع القائمة"
              className="absolute top-5 -start-3 w-6 h-6 rounded-full bg-[#004EEB] border border-[#0040cc] flex items-center justify-center text-white shadow-md hover:bg-[#0055ff] transition-colors z-10"
            >
              <ChevronsLeft className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="sidebar-divider mx-3" />

        <NavItems isCollapsed={isCollapsed} />

        {/* User footer */}
        <div className="shrink-0 px-3 pb-4">
          {isCollapsed ? (
            <div className="relative group/user flex justify-center">
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="w-9 h-9 rounded-xl bg-[#004EEB]/70 grid place-items-center text-white text-sm font-bold hover:bg-red-500/80 transition-colors"
                title="تسجيل الخروج"
              >
                {initials}
              </button>
              <div className="pointer-events-none absolute end-full top-1/2 -translate-y-1/2 me-3 z-50 hidden group-hover/user:flex items-center">
                <div className="bg-[#1a2744] border border-white/10 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap">
                  {user?.name} · تسجيل الخروج
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[var(--bg-card-hover)]/[0.04] transition-colors group/user cursor-default">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#004EEB]/80 to-[#004EEB]/40 grid place-items-center text-white text-xs font-bold shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white/90 text-[12px] font-semibold truncate leading-tight">{user?.name}</div>
                <div className="text-white/30 text-[10px] truncate">{user?.email}</div>
              </div>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                title="تسجيل الخروج"
                className="p-1.5 rounded-lg text-white/25 hover:text-red-400 hover:bg-red-500/10 transition-all opacity-0 group-hover/user:opacity-100"
              >
                <LogOut className="w-3.5 h-3.5 rtl-flip" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── Mobile drawer ── */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <aside
            className={`md:hidden fixed inset-y-0 end-0 z-50 w-[82%] max-w-[300px] ${sidebarBg} flex flex-col border-s border-white/[0.07]`}
            data-testid="mobile-drawer"
            dir="rtl"
          >
            <div className="relative h-16 flex items-center gap-3 px-4 border-b border-white/[0.07]">
              <div className="w-9 h-9 bg-[#004EEB] rounded-xl grid place-items-center shadow-[0_0_16px_rgba(0,78,235,0.4)]">
                <PlatformLogo size={22} />
              </div>
              <div className="flex-1 leading-tight">
                <div className="text-white font-black text-[15px] tracking-tight">{name}<span className="text-[#004EEB]">.</span></div>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-menu-close"
                className="p-1.5 rounded-lg text-white/40 hover:bg-[var(--bg-card-hover)]/10 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <NavItems isCollapsed={false} onNavigate={() => setMobileOpen(false)} />
            <div className="sidebar-divider mx-3" />
            <div className="px-3 pb-4 pt-2">
              <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl group/user">
                <div className="w-8 h-8 rounded-xl bg-[#004EEB]/70 grid place-items-center text-white text-xs font-bold shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white/90 text-[12px] font-semibold truncate">{user?.name}</div>
                  <div className="text-white/30 text-[10px] truncate">{user?.email}</div>
                </div>
                <button data-testid="logout-button-mobile" onClick={handleLogout}
                  className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                  <LogOut className="w-4 h-4 rtl-flip" />
                </button>
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
